<template>
  <nav v-if="displayingPageNums.length > 1" class="pagination-container" aria-label="pagination navigation">
    <div class="pagination">
      <!-- if is used for server side rendering -->
      <template v-if="!isSPA">
        <!-- to previous -->
        <a
          class="chevron"
          :aria-disabled="currentPage - 1 <= 0"
          :class="{ disabled: currentPage - 1 <= 0 }"
          :href="currentPage - 1 > 0 ? thisTarget(currentPage - 1) : null"
          :aria-label="currentPage <= 1 ? 'No further previous page' : ariaLabel(currentPage - 1)"
          :title="currentPage <= 1 ? 'No further previous page' : ariaLabel(currentPage - 1)"
        >
          <svg><use xlink:href="/assets/public/images/icons.svg#previous-arrow" /></svg>
          <p class="chevron-text">Previous</p>
        </a>

        <ol class="numbers-container-desktop">
          <!-- page numbers. note: we are only showing 3 numbers at a time -->
          <li v-for="pageNum in displayingPageNums" :key="ariaLabel(pageNum)">
            <!-- displaying ellipsis -->
            <span v-if="pageNum === '...'">{{ pageNum }}</span>
            <a
              v-else
              :class="{ current: isCurrentPage(pageNum), number: true }"
              :href="isCurrentPage(pageNum) ? null : thisTarget(pageNum)"
              :aria-label="isCurrentPage(pageNum) ? `Current page, page ${pageNum}` : ariaLabel(pageNum)"
              :title="isCurrentPage(pageNum) ? `Current page, page ${pageNum}` : ariaLabel(pageNum)"
              :aria-current="isCurrentPage(pageNum)"
            >
              {{ pageNum }}
            </a>
          </li>
        </ol>
        <p class="numbers-container-mobile">Page {{ currentPage }} of {{ endPage }}</p>
        <!-- to next -->
        <a
          class="chevron"
          :aria-disabled="currentPage >= pageNums.length"
          :class="{ disabled: currentPage >= pageNums.length }"
          :href="currentPage >= pageNums.length ? null : thisTarget(currentPage + 1)"
          :aria-label="currentPage >= pageNums.length ? 'No further next page' : ariaLabel(currentPage + 1)"
          :title="currentPage >= pageNums.length ? 'No further next page' : ariaLabel(currentPage + 1)"
        >
          <p class="chevron-text">Next</p>
          <svg><use xlink:href="/assets/public/images/icons.svg#next-arrow" /></svg>
        </a>
      </template>

      <!-- if is used for SPA -->
      <template v-else>
        <!-- to previous -->
        <button
          class="chevron non-button-style"
          :class="{ disabled: currentPage - 1 <= 0 }"
          :aria-disabled="currentPage - 1 <= 0"
          :disabled="currentPage - 1 <= 0"
          @click="currentPage - 1 > 0 ? emitPageNumber(currentPage - 1) : null"
          :aria-label="currentPage <= 1 ? 'No further previous page' : ariaLabel(currentPage - 1)"
          :title="currentPage <= 1 ? 'No further previous page' : ariaLabel(currentPage - 1)"
        >
          <svg><use xlink:href="/assets/public/images/icons.svg#previous-arrow" /></svg>
          <p class="chevron-text">Previous</p>
        </button>

        <!-- page numbers. note: we are only showing 3 numbers at a time -->
        <ol class="numbers-container-desktop">
          <li v-for="pageNum in displayingPageNums" :key="ariaLabel(pageNum)">
            <!-- displaying ellipsis -->
            <span v-if="pageNum === '...'">{{ pageNum }}</span>
            <button
              v-else
              class="non-button-style"
              :class="{ current: isCurrentPage(pageNum), number: true }"
              @click="isCurrentPage(pageNum) ? null : emitPageNumber(pageNum)"
              :aria-label="isCurrentPage(pageNum) ? `Current page, page ${pageNum}` : ariaLabel(pageNum)"
              :title="isCurrentPage(pageNum) ? `Current page, page ${pageNum}` : ariaLabel(pageNum)"
              :aria-current="isCurrentPage(pageNum)"
            >
              {{ pageNum }}
            </button>
          </li>
        </ol>
        <template>
          <div class="numbers-container-mobile">
            <p class="" v-if="!$slots.paginationLabel">Page {{ currentPage }} of {{ endPage }}</p>
            <slot v-else name="paginationLabel" />
          </div>
        </template>

        <!-- to next -->
        <button
          class="chevron non-button-style"
          :aria-disabled="currentPage >= pageNums.length"
          :disabled="currentPage >= pageNums.length"
          :class="{ disabled: currentPage >= pageNums.length }"
          @click="emitPageNumber(currentPage + 1)"
          :aria-label="currentPage >= pageNums.length ? 'No further next page' : ariaLabel(currentPage + 1)"
          :title="currentPage >= pageNums.length ? 'No further next page' : ariaLabel(currentPage + 1)"
        >
          <p class="chevron-text">Next</p>
          <svg><use xlink:href="/assets/public/images/icons.svg#next-arrow" /></svg>
        </button>
      </template>
    </div>
    <div v-if="$slots.paginationLabel" class="pagination-label">
      <slot name="paginationLabel" />
    </div>
  </nav>
</template>

<script>
import { getQueryString, setQueryString } from '@/utils/UrlUtils';
import { isBuildCRM } from '@/utils';

// constant for this component only
const pageNumberURLParamName = 'pageNum';
const isCRM = isBuildCRM();

export default {
  name: 'pagination',

  props: {
    /**
     * Integer of last page number. for BE, it can also accept String too
     * This will be used to generate array of integers, which represent pages.
     *
     * eg: if `5` is given, it indicates there are total 5 pages and component will create [1,2,3,4,5]
     */
    endPage: {
      type: [Number, String],
      required: true,
    },

    /**
     * If true, it will consider this pagination is used for SPA hence clicking each page will emit clicked page number with `toPageNumber`
     */
    isSPA: {
      type: Boolean,
      default: false,
    },

    /**
     * If selected page number of pagination need to be changed via data from parent component, this would be required.
     * If given, whenever this prop value changes, pagination will change its current highlighted page number to match with given value.
     */
    currentPageFromParent: {
      type: [Number, String],
    },

    /**
     * If true then allows for setting query string
     * Disables query string if false (used in CRM)
     */
    changeQueryString: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      currentPage: null,
    };
  },

  beforeMount() {
    if (!isCRM) {
      this.setCurrentPage();
    } else {
      this.currentPage = 1;
    }
  },

  computed: {
    pageNums() {
      return Array.from(new Array(Number(this.endPage)), (x, index) => {
        return index + 1;
      });
    },

    displayingPageNums() {
      if (this.pageNums.length <= 5) {
        return this.pageNums.slice(0, this.pageNums.length);
      }

      if (this.currentPage >= this.pageNums.length - 2) {
        return [1, '...', ...this.pageNums.slice(this.pageNums.length - 3, this.pageNums.length)];
      }
      if (this.currentPage >= this.pageNums.length - 5) {
        return this.pageNums.slice(this.currentPage - 1, this.pageNums.length);
      }

      return [...this.pageNums.slice(this.currentPage - 1, this.currentPage + 2), '...', this.pageNums.length];
    },
  },

  watch: {
    currentPageFromParent(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.currentPage = Number(newVal);
      }
    },
  },

  methods: {
    setCurrentPage() {
      const currentPageNumberFromURLParam = Number(getQueryString(pageNumberURLParamName));

      // if page number query param is not present or not a number or equal to 0 or less then 0, consider current page number is 1
      if (currentPageNumberFromURLParam <= 0 || Number.isNaN(currentPageNumberFromURLParam)) {
        if (this.changeQueryString) {
          setQueryString(pageNumberURLParamName, 1);
        }
        this.currentPage = 1;
      } else {
        // if page number query param has decimals, return round down to nearest whole number (if given 5.01 or 5.95, return 5)
        this.currentPage = Math.floor(currentPageNumberFromURLParam);
      }
    },

    ariaLabel(pageNum) {
      return `Go to page number ${pageNum}`;
    },

    isCurrentPage(pageNum) {
      return pageNum === this.currentPage;
    },

    thisTarget(pageNum) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.set(pageNumberURLParamName, pageNum);

      return `${window.location.pathname}?${urlParams.toString()}`;
    },

    emitPageNumber(pageNum) {
      if (!isCRM) {
        if (this.changeQueryString) {
          // update url query param
          setQueryString(pageNumberURLParamName, pageNum);
        }
        this.setCurrentPage();
      } else {
        this.currentPage = pageNum;
      }

      // emit clicked page number
      this.$emit('toPageNumber', pageNum);
    },
  },
};
</script>

<style lang="scss" scoped>
.pagination-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: $SM;
  margin: $BASE 0;

  p {
    margin: 0;
    word-break: normal;
  }

  .pagination {
    @include flex-row;
    align-items: center;
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    column-gap: $BASE;
    width: 100%;

    font-family: $FONT_FAMILY_BRAND;
    font-size: $FONT_SIZE_BASE;
    font-weight: $FONT_WEIGHT_MEDIUM;
    line-height: $LINE_HEIGHT_NORMAL;

    a,
    button.non-button-style {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;

      color: $NEUTRAL_MIDNIGHT;
      text-decoration: none;

      border: 1.5px solid transparent;
      background-color: transparent;

      transition: background-color $TRANSITION_TIMING ease, color $TRANSITION_TIMING ease, box-shadow $TRANSITION_TIMING ease;

      &:focus-visible {
        outline: 1.5px solid $PRIMARY_GREEN_100;
        outline-offset: 4px;
      }

      &.disabled,
      &:disabled,
      &[aria-disabled='true'] {
        cursor: not-allowed;
        color: $PRIMARY_GREY_60;
        pointer-events: none;
      }

      &.number {
        width: $LG;
        height: $LG;
        border: 1.5px solid $PRIMARY_GREY_20;
        border-radius: $ROUND;
        background-color: $NEUTRAL_WHITE;
        line-height: 1;

        &:not([aria-disabled='true']):not(.current):not(:disabled) {
          &:hover {
            background-color: $PRIMARY_BRIGHT_GREEN_20;
            border-color: $PRIMARY_BRIGHT_GREEN_60;
          }
        }
      }

      &.current {
        background-color: $PRIMARY_MEDIUM_GREEN_100;
        border-color: $PRIMARY_MEDIUM_GREEN_100;
        color: $NEUTRAL_WHITE;
        font-weight: $FONT_WEIGHT_STRONG;
        cursor: default;
      }

      &.chevron {
        @include flex-row;
        align-items: center;
        justify-content: center;
        column-gap: $SM;

        width: fit-content;
        min-height: $LG;
        padding: $SM $BASE;
        border-radius: $ROUND;
        border: 1.5px solid $PRIMARY_GREY_20;
        background-color: $NEUTRAL_WHITE;

        &:hover:not(.disabled):not(:disabled):not([aria-disabled='true']) {
          background-color: $PRIMARY_BRIGHT_GREEN_20;
          border-color: $PRIMARY_BRIGHT_GREEN_60;
        }

        .chevron-text {
          @include mobile {
            display: none;
          }
        }

        svg {
          height: 20px;
          width: 20px;
          max-width: unset;
          color: currentColor;

          use {
            fill: currentColor;
          }
        }
      }
    }

    & > .chevron:first-child {
      grid-column: 1;
      justify-self: start;
    }

    & > .chevron:last-child {
      grid-column: 3;
      justify-self: end;
    }
  }

  .numbers-container-desktop {
    @include flex-row;
    align-items: center;
    justify-content: center;

    margin: 0;
    padding: 0;
    list-style: none;
    width: fit-content;
    max-width: 100%;
    flex-wrap: wrap;
    column-gap: $SM;
    row-gap: $SM;

    grid-column: 2;
    justify-self: center;

    font-family: $FONT_FAMILY_BRAND;
    font-size: $FONT_SIZE_BASE;
    font-weight: $FONT_WEIGHT_MEDIUM;
    line-height: $LINE_HEIGHT_NORMAL;

    li {
      list-style: none;

      ::before {
        content: none !important;
      }

      span {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: $LG;
        height: $LG;
        color: $PRIMARY_GREY_60;
      }
    }
  }

  .numbers-container-mobile {
    display: none;
    color: $NEUTRAL_MIDNIGHT;

    grid-column: 2;
    justify-self: center;
  }

  .pagination-label {
    padding: $SM calc(#{$LG} + #{$SM});
    line-height: $LINE_HEIGHT_NORMAL;
    font-weight: $FONT_WEIGHT_MEDIUM;
    color: $NEUTRAL_MIDNIGHT;

    @include mobile {
      display: none;
    }
  }

  @include mobile {
    .numbers-container-desktop {
      display: none;
    }

    .numbers-container-mobile {
      display: block;
    }
  }
}
</style>